import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { CardImage } from './CardImage';

function SlideMedia({
  slide,
  alt,
  aspectRatio,
  rounded,
  inactive,
  videoRef,
}) {
  if (slide.type === 'video') {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio,
          borderRadius: rounded,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#111',
          border: 1,
          borderColor: (theme) =>
            theme.palette.mode === 'dark'
              ? alpha('#D4A574', 0.18)
              : alpha('#B8894A', 0.14),
        }}
      >
        <Box
          component="video"
          ref={videoRef}
          src={slide.src}
          controls
          playsInline
          preload="metadata"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            filter: inactive ? 'grayscale(0.65) opacity(0.55)' : 'none',
          }}
        />
      </Box>
    );
  }

  return (
    <CardImage
      src={slide.src}
      alt={alt}
      aspectRatio={aspectRatio}
      rounded={rounded}
      iconSize={48}
      fit="contain"
      inactive={inactive}
      padded
    />
  );
}

export default function ProductImageSlider({
  images = [],
  videoUrl = null,
  alt = '',
  inactive = false,
  aspectRatio = '1 / 1',
  rounded = 3,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isRtl = theme.direction === 'rtl';
  const videoRef = useRef(null);

  const slides = useMemo(() => {
    const list = (images.length ? images : [])
      .filter(Boolean)
      .map((src) => ({ type: 'image', src }));
    if (videoUrl) {
      list.push({ type: 'video', src: videoUrl });
    }
    return list;
  }, [images, videoUrl]);

  const multi = slides.length > 1;
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const slidesKey = slides.map((s) => `${s.type}:${s.src}`).join('|');

  useEffect(() => {
    setIndex(0);
  }, [slidesKey]);

  useEffect(() => {
    setIndex((prev) => Math.min(prev, Math.max(0, slides.length - 1)));
  }, [slides.length]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (slides[index]?.type !== 'video') {
      video.pause();
    }
  }, [index, slides]);

  if (!slides.length) {
    return (
      <CardImage
        src={null}
        alt={alt}
        aspectRatio={aspectRatio}
        rounded={rounded}
        iconSize={48}
        fit="contain"
        inactive={inactive}
        padded
      />
    );
  }

  if (!multi) {
    return (
      <SlideMedia
        slide={slides[0]}
        alt={alt}
        aspectRatio={aspectRatio}
        rounded={rounded}
        inactive={inactive}
        videoRef={videoRef}
      />
    );
  }

  const goTo = (next) => {
    const len = slides.length;
    setIndex(((next % len) + len) % len);
  };

  const goPrev = () => goTo(index - 1);
  const goNext = () => goTo(index + 1);
  const PrevIcon = isRtl ? ChevronRightIcon : ChevronLeftIcon;
  const NextIcon = isRtl ? ChevronLeftIcon : ChevronRightIcon;

  const navButtonSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    width: { xs: 36, sm: 42 },
    height: { xs: 36, sm: 42 },
    bgcolor: isDark ? alpha('#1e1e1e', 0.88) : alpha('#FFF9F0', 0.92),
    border: 1,
    borderColor: isDark ? alpha('#D4A574', 0.28) : alpha('#B8894A', 0.22),
    color: 'primary.main',
    boxShadow: isDark
      ? '0 6px 18px rgba(0,0,0,0.35)'
      : '0 6px 18px rgba(184,137,74,0.18)',
    '&:hover': {
      bgcolor: isDark ? '#1e1e1e' : '#FFF9F0',
      borderColor: 'primary.main',
    },
  };

  const activeSlide = slides[index];

  return (
    <Box>
      <Box
        sx={{ position: 'relative', borderRadius: rounded, overflow: 'hidden' }}
        onTouchStart={(e) => {
          touchStartX.current = e.changedTouches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null) return;
          const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
          const delta = endX - touchStartX.current;
          touchStartX.current = null;
          if (Math.abs(delta) < 40) return;
          const swipedLeft = delta < 0;
          if (isRtl) {
            if (swipedLeft) goPrev();
            else goNext();
          } else if (swipedLeft) {
            goNext();
          } else {
            goPrev();
          }
        }}
      >
        <SlideMedia
          slide={activeSlide}
          alt={`${alt} (${index + 1})`}
          aspectRatio={aspectRatio}
          rounded={rounded}
          inactive={inactive}
          videoRef={videoRef}
        />

        <IconButton
          aria-label="previous image"
          onClick={goPrev}
          sx={{ ...navButtonSx, insetInlineStart: { xs: 6, sm: 10 } }}
        >
          <PrevIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="next image"
          onClick={goNext}
          sx={{ ...navButtonSx, insetInlineEnd: { xs: 6, sm: 10 } }}
        >
          <NextIcon fontSize="small" />
        </IconButton>

        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            px: 1.1,
            py: 0.55,
            borderRadius: 999,
            bgcolor: alpha('#2C2416', isDark ? 0.55 : 0.38),
            backdropFilter: 'blur(6px)',
            border: `1px solid ${alpha('#fff', 0.12)}`,
          }}
        >
          {slides.map((slide, i) => (
            <Box
              key={`${slide.type}-${i}`}
              component="button"
              type="button"
              aria-label={slide.type === 'video' ? 'video' : `image ${i + 1}`}
              onClick={() => setIndex(i)}
              sx={{
                width: i === index ? 18 : 8,
                height: 8,
                border: 0,
                p: 0,
                borderRadius: 999,
                cursor: 'pointer',
                bgcolor: i === index ? '#F0D4A8' : 'rgba(255,255,255,0.5)',
                transition: 'width 0.2s ease, background-color 0.2s ease',
              }}
            />
          ))}
        </Stack>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        justifyContent="center"
        sx={{
          mt: 1.75,
          overflowX: 'auto',
          pb: 0.5,
          px: 0.25,
          scrollbarWidth: 'thin',
        }}
      >
        {slides.map((slide, i) => {
          const selected = i === index;
          return (
            <Box
              key={`${slide.type}-${slide.src}-${i}`}
              component="button"
              type="button"
              onClick={() => setIndex(i)}
              aria-label={slide.type === 'video' ? 'video thumbnail' : `thumbnail ${i + 1}`}
              sx={{
                flex: '0 0 auto',
                width: { xs: 60, sm: 68 },
                height: { xs: 60, sm: 68 },
                p: 0.65,
                borderRadius: 2.25,
                border: 2,
                borderColor: selected
                  ? 'primary.main'
                  : (isDark ? alpha('#D4A574', 0.2) : alpha('#B8894A', 0.16)),
                bgcolor: isDark ? alpha('#D4A574', 0.08) : alpha('#B8894A', 0.05),
                backgroundImage: isDark
                  ? 'linear-gradient(160deg, rgba(212,165,116,0.12), rgba(30,30,30,0.7))'
                  : 'linear-gradient(160deg, #FFF9F0, rgba(240,212,168,0.28))',
                cursor: 'pointer',
                overflow: 'hidden',
                boxShadow: selected
                  ? (isDark
                    ? '0 4px 14px rgba(0,0,0,0.4)'
                    : '0 4px 14px rgba(184,137,74,0.22)')
                  : 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                transform: selected ? 'translateY(-1px)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {slide.type === 'video' ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 1.5,
                    bgcolor: alpha('#2C2416', 0.85),
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <PlayArrowIcon sx={{ color: '#F0D4A8', fontSize: 28 }} />
                </Box>
              ) : (
                <Box
                  component="img"
                  src={slide.src}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                    filter: inactive ? 'grayscale(0.65) opacity(0.55)' : 'none',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
